<template>
    <div ref="vJoySubmitRoot">
        <VJoyTooltip v-if="tooltip || slots.tooltip" variant="secondary">
            <template #default>
                <VJoyButton
                    v-bind="{...reactive(vJoyButtonProps), ...$attrs}"
                    :size="size"
                    :variant="variant"
                    :loading="computedLoading"
                    :disabled="disabled"
                    :data-testid="dataTestid"
                    @click="handleClick"
                >
                    <slot />
                </VJoyButton>
            </template>
            <!-- Bug in VJoyTooltip when using v-if on the tooltip content slot: display an empty tooltip -->
            <template #tooltip-content>
                <slot v-if="slots.tooltip" name="tooltip" />
                <span v-else-if="tooltip">{{ tooltip }}</span>
            </template>
        </VJoyTooltip>
        <VJoyButton
            v-else
            v-bind="{...reactive(vJoyButtonProps), ...$attrs}"
            :size="size"
            :variant="variant"
            :loading="computedLoading"
            :disabled="disabled"
            :data-testid="dataTestid"
            @click="handleClick"
        >
            <slot />
        </VJoyButton>
    </div>
</template>

<script setup lang="ts">
    import {computed, onBeforeUnmount, onMounted, reactive, ref, toRefs, useTemplateRef, watch, type ButtonHTMLAttributes, type VNode} from 'vue';
    import type {ComponentProps} from 'vue-component-type-helpers';
    import {useLogger} from '#imports';
    import {useErrorHandler} from '@malt/utils';
    import {VJoyButton, VJoyTooltip, type TJoyButtonSizes, type TJoyButtonVariants} from '@maltjoy/core-vue';

    interface SubmitButtonProps extends /* @vue-ignore ComponentProps type can't compile into runtime props  */ ComponentProps<typeof VJoyButton> {
        /**
         * Describes a Promise action to execute when the button is clicked.
         * It will apply the loading state automatically.
         */
        action: () => Promise<unknown> | unknown;
        /**
         * Display text on a tooltip on hover.
         */
        tooltip?: string;
        dataTestid?: string;
        /**
         * Disable the button.
         */
        disabled?: boolean;
        /**
         * Specifies which form the button belongs to
         * */
        form?: string;
        // - Following props need to be manually declared for "withDefaults" to work
        variant?: TJoyButtonVariants;
        type?: ButtonHTMLAttributes['type'];
        size?: TJoyButtonSizes;
        /**
         * Controlled loading state of the button.
         * When omitted, VJoySubmit automatically displays loading while the action is pending.
         */
        loading?: boolean;
    }

    const props = withDefaults(defineProps<SubmitButtonProps>(), {
        variant: 'main',
        type: 'submit',
        size: 'medium',
        loading: undefined,
    });
    const {tooltip, action, variant, disabled, dataTestid, ...vJoyButtonProps} = toRefs(props);

    const vJoySubmitRoot = useTemplateRef('vJoySubmitRoot');

    const slots = defineSlots<{
        default: () => VNode;
        /**
         * Display content on a tooltip on hover.
         */
        tooltip?: () => VNode;
    }>();

    const logger = useLogger();
    const {handleUnknownError} = useErrorHandler();

    const buttonId = computed(() => vJoyButtonProps.id?.value || dataTestid?.value || 'unknown');

    // - Disabled state

    watch(
        disabled,
        () => {
            if (import.meta.dev && disabled.value && !(tooltip.value || slots.tooltip)) {
                logger.warn(
                    `[VJoySubmit]` +
                        `\n Component SubmitButton "${buttonId.value}" is disabled but no tooltip is provided.` +
                        `\n This is not recommended as it will be confusing for the user.` +
                        `\n\n Use either the "tooltip" prop or the "tooltip" slot to explain why it's disabled to the user.`,
                );
            }
        },
        {immediate: true},
    );

    // - Loading state

    const localLoading = ref(false);

    const computedLoading = computed(() => props.loading ?? localLoading.value);

    function isAssociatedWithForm(): boolean {
        if (props.form) {
            return true;
        }

        return !!vJoySubmitRoot.value?.closest('form');
    }

    function isLinkedForm(form: HTMLFormElement): boolean {
        if (props.form) {
            return form.id === props.form;
        }

        return vJoySubmitRoot.value?.closest('form') === form;
    }

    function handleClick(event: MouseEvent) {
        if (isAssociatedWithForm()) {
            return;
        }

        if (!computedLoading.value) {
            handleSubmit(event);
        }
    }

    async function handleSubmit(event?: SubmitEvent | MouseEvent) {
        event?.preventDefault();
        event?.stopPropagation();
        if (action.value) {
            localLoading.value = true;
            try {
                await action.value();
            } catch (error) {
                handleUnknownError(error, {showSnackbar: false});
            } finally {
                localLoading.value = false;
            }
        }
    }

    // Form events handling — capture submit at document level so deferred forms work without re-wiring
    function handleDocumentSubmit(event: SubmitEvent) {
        const form = event.target;

        if (!(form instanceof HTMLFormElement) || !isLinkedForm(form)) {
            return;
        }

        void handleSubmit(event);
    }

    onMounted(() => {
        document.addEventListener('submit', handleDocumentSubmit, true);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('submit', handleDocumentSubmit, true);
    });

    defineExpose({
        localLoading,
    });
</script>
